import { USER_TYPES } from '@medflex/utils/constants';
import { TRACKING_ACTIONS, TRACKING_ELEMENT_TYPES, trackEvent } from '@medflex/utils/Tracking';
import Logger from '@medflex/utils/Logger';
import makeApiRequest from '../../../api/makeApiRequest';

const normalizeMessage = ({ id, attributes }) => ({ id, ...attributes });

let retryCount = 0;

const ContactMessages = {
  name: 'contactMessages',
  state: {
    messages: [],
    user: {},
    contact: null,
    autoResponseMessage: '',
  },
  reducers: {
    onLoaded(state, { messages, contact, autoResponseMessage }) {
      return { ...state, messages, contact, autoResponseMessage };
    },
    onMarkedImportant(state, { id, important }) {
      const { messages } = state;
      return {
        ...state,
        messages: messages.map(message => {
          if (message.id === id) {
            const { markedImportantAt, ...rest } = message;
            if (important) return { ...rest, markedImportantAt: Date.now() };
            return rest;
          }
          return message;
        }),
      };
    },
    onMarkedChecked(state, { id, checked }) {
      const { messages } = state;
      return {
        ...state,
        messages: messages.map(message => {
          if (message.id === id) {
            const { checkedAt, checkedBy, ...rest } = message;
            if (checked) return { ...rest, checkedAt, checkedBy };
            return rest;
          }
          return message;
        }),
      };
    },
    onDelete(state, { id }) {
      const { messages } = state;
      return {
        ...state,
        messages: messages.map(m => (m.id === id ? { ...m, deletedAt: Date.now() } : m)),
      };
    },
    onMessage(state, message) {
      const { contactId, id: messageId } = message;
      if (state.contact && state.contact.id === contactId) {
        if (!state.messages.some(({ id }) => id === messageId)) {
          return {
            ...state,
            messages: [...state.messages, message],
            // update lastSeenMessageTimestampe (useful in case a user writes a message
            // before markAsRead has been triggered)
            ...(!message.sender.meOrSurrogate &&
              message.time && {
                contact: {
                  ...state.contact,
                  user: {
                    ...state.contact.user,
                    lastSeenMessageTimestamp: message.time,
                  },
                },
              }),
          };
        }
      }
      return state;
    },
    onMessageModified(state, message) {
      const { contactId, markedImportantAt, checkedAt, checkedBy, id } = message;
      if (state.contact && state.contact.id === contactId) {
        return {
          ...state,
          messages: state.messages.map(message => {
            if (message.id === id) {
              return {
                ...message,
                markedImportantAt,
                checkedAt,
                checkedBy,
              };
            }

            return message;
          }),
        };
      }
      return state;
    },
    onMessageMulticast(state, messages) {
      if (state.contact) {
        const chatMessage = messages.find(
          ({ contactId }) => state.contact && state.contact.id === contactId,
        );
        if (!state.messages.some(({ id }) => id === chatMessage.id)) {
          return {
            ...state,
            messages: [...state.messages, chatMessage],
            ...(!chatMessage.sender.meOrSurrogate &&
              chatMessage.time && {
                contact: {
                  ...state.contact,
                  user: {
                    ...state.contact.user,
                    lastSeenMessageTimestamp: chatMessage.time,
                  },
                },
              }),
          };
        }
      }

      return state;
    },
    onMessageDeleted(state, message) {
      return {
        ...state,
        messages: state.messages.map(m => (m.id === message.id ? { ...m, ...message } : m)),
      };
    },
    onMessageSeen(state, { id, lastSeenMessageTimestamp }) {
      if (state.contact && state.contact.id === id) {
        return {
          ...state,
          contact: {
            ...state.contact,
            user: {
              ...state.contact.user,
              lastSeenMessageTimestamp,
            },
          },
        };
      }
      return state;
    },
  },
  messageListeners: {
    MESSAGE_CREATED({ payload }) {
      this.onMessage(payload);
    },
    MESSAGE_MODIFIED({ payload }) {
      this.onMessageModified(payload);
    },
    MESSAGE_DELETED({ payload }) {
      this.onMessageDeleted(payload);
    },
    CONTACT_LAST_SEEN_MSG({ payload }) {
      this.onMessageSeen(payload);
    },
    MESSAGE_MULTICAST_CREATED({ payload }) {
      this.onMessageMulticast(payload);
    },
  },
  effects: dispatch => ({
    async find({ contactId }, { oidcSession, chatContacts: contacts }) {
      const url = `/contacts/${contactId}/messages`;
      const [request] = makeApiRequest(url, oidcSession);
      const { error, data } = await request;
      if (!error) {
        dispatch.errors.remove({ realm: 'chat_messages', type: 'load' });
        try {
          const contact = contacts.find(c => c.id === contactId);
          return this.onLoaded({
            contact,
            messages: data.data.messages.map(normalizeMessage),
            autoResponseMessage: data.data.autoResponseMessage,
          });
        } catch (e) {
          Logger.error(`messages.load(): ${e.message}`);
        }
      }

      dispatch.errors.add({ realm: 'chat_messages', type: 'load', error });
      throw error;
    },
    async send(payload, { oidcSession, superuser }) {
      const url = '/messages';
      const body = {
        data: {
          type: 'messages',
          attributes: {
            sender: superuser.id,
            ...payload,
          },
        },
      };

      /** @type any */
      const [request] = makeApiRequest({ url, method: 'post', data: body }, oidcSession);

      // calling a reducer not necessary here as the
      // web socket message will trigger it already
      const { error, ...rs } = await request;

      if (!error) {
        retryCount = 0;

        if (payload.files) {
          dispatch.errors.remove({ realm: 'chat_messages', type: 'sendFile' });
        } else {
          dispatch.errors.remove({ realm: 'chat_messages', type: 'send' });
        }
      } else {
        if (payload.files) {
          if (rs.status === 424 && retryCount < 6) {
            retryCount += 1;
            setTimeout(() => this.send(payload), 500);
            dispatch.errors.add({ realm: 'chat_messages', type: 'sendFile', error });
            return;
          }
          dispatch.errors.add({ realm: 'chat_messages', type: 'sendFile', error });
        } else {
          dispatch.errors.add({ realm: 'chat_messages', type: 'send', error });
        }
        throw error;
      }
    },
    async sendMulticast(payload, { oidcSession, superuser }) {
      const url = '/messages/multicast';
      const body = {
        data: {
          type: 'messages',
          attributes: {
            sender: superuser.id,
            ...payload,
          },
        },
      };

      const [request] = makeApiRequest({ url, method: 'post', data: body }, oidcSession);

      const { error, rs } = await request;

      if (error) {
        if (rs.status === 424 && retryCount < 6) {
          retryCount += 1;
          setTimeout(() => this.sendMulticast(payload), 500);
          dispatch.errors.add({ realm: 'chat_messages', type: 'sendMulticast', error });
          return;
        }
        throw error;
      }

      retryCount = 0;
      dispatch.errors.remove({ realm: 'chat_messages', type: 'sendMulticast' });
    },
    async markImportant({ id, contactId, important }, { oidcSession }) {
      const url = `messages/${id}/important`;
      const data = {
        data: { type: 'messages', id, attributes: { important } },
      };
      const [request] = makeApiRequest({ url, method: 'patch', data }, oidcSession);
      const { error } = await request;
      if (!error) this.onMarkedImportant({ id, important, contactId });
      else throw error;
    },
    async markChecked({ id, checked }, { oidcSession }) {
      const url = `messages/${id}/checked`;
      const data = {
        data: { type: 'messages', id, attributes: { checked } },
      };
      const [request] = makeApiRequest({ url, method: 'patch', data }, oidcSession);
      const { error } = await request;
      if (error) throw error;
    },
    async delete({ id }, { oidcSession }) {
      const url = `messages/${id}`;

      const [request] = makeApiRequest({ url, method: 'delete' }, oidcSession);
      const { error } = await request;

      if (!error) {
        trackEvent({
          zone: 'Chat',
          action: TRACKING_ACTIONS.CLICK,
          elementType: TRACKING_ELEMENT_TYPES.BUTTON,
          actionText: 'Delete Message - Success',
        });
        this.onDelete({ id });
      } else {
        trackEvent({
          zone: 'Chat',
          action: TRACKING_ACTIONS.CLICK,
          elementType: TRACKING_ELEMENT_TYPES.BUTTON,
          actionText: 'Delete Message - Failed',
        });
        throw error;
      }
    },
    sharePatientContact(payload, { oidcSession, superuser }) {
      const url = '/messages';
      const body = {
        data: {
          type: 'messages',
          attributes: {
            type: USER_TYPES.PATIENT,
            sender: superuser.id,
            ...payload,
          },
        },
      };
      const [request] = makeApiRequest({ url, method: 'post', data: body }, oidcSession);

      return request
        .then(({ error, data, headers }) => {
          if (error) throw error;
          dispatch.errors.remove({ realm: 'chat_messages', type: 'share_patient_contact' });
          return { data, headers };
        })
        .catch(error => {
          dispatch.errors.add({ realm: 'chat_messages', type: 'share_patient_contact', error });
          throw error;
        });
    },
  }),
};

export default ContactMessages;
